/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "@/components/Button/Button";
import Dialog from "@/components/Dialog/Dialog";
import { Icon } from "@/components/Icon";
import { ToastContext } from "@/components/Toast/ToastProvider";
import { type FC, useState, useCallback, useContext } from "react";
import { Slot } from "utils/create-slots";
import * as gtag from "@/lib/gtag";
import { useIsomorphicLayoutEffect } from "hooks/useIsomorphicLayoutEffect";

const NativeSharePopUp: FC<Props> = ({
  shareData,
  openPopup,
  whatsapp,
  mail,
  facebook,
  linkedin,
  popupHeadline,
  onClose,
  onError,
}) => {
  const [state, setState] = useState<ShareState>("pending");
  const { addToast, removeToast } = useContext(ToastContext);
  const [copyText, setCopyText] = useState(false);

  const [saved, setSaved] = useState(
    localStorage.getItem("wishlist")
      ? JSON.parse(localStorage.getItem("wishlist")!).name
      : ""
  );

  useIsomorphicLayoutEffect(() => {
    if (localStorage.getItem("wishlist"))
      setSaved(JSON.parse(localStorage.getItem("wishlist")!).name);
  }, []);

  const SHARE_LINK = shareData?.url || "";
  const SHARE_TEXT = shareData?.text || "";
  const SHARE_SUBJECT = shareData?.text || "";

  const handleTrackingShare = (clientName) => {
    gtag.event({
      event: "itemClick",
      itemClick: "share",
      position: `${clientName}`,
    });
  };

  const handleShareClick = (
    shareType: "mail" | "whatsapp" | "clipboard" | "linkedin" | "facebook",
    name: string
  ) => {
    switch (shareType) {
      case "mail":
        shareOnMailClient();
        break;
      case "whatsapp":
        shareOnWhatsApp();
        break;
      case "linkedin":
        shareOnLinkedIn();
        break;
      case "facebook":
        shareOnFacebook();
        break;
      case "clipboard":
      default:
        saveToClipboard();
    }
    setSaved(name);
    handleTrackingShare(shareType);
  };

  const shareOnWhatsApp = useCallback(() => {
    // return (
    //   "https://" +
    //   (isMobileOrTablet() ? "api" : "web") +
    //   ".whatsapp.com/send" +
    //   objectToGetParams({
    //     text: title ? title + separator + url : url,
    //   })
    // );
    // `whatsapp://send?text=${encodeURI(SHARE_TEXT)}${encodeURI(SHARE_LINK)}`
    // `https://web.whatsapp.com/send?text=${encodeURI(SHARE_TEXT)}&url=${encodeURI(SHARE_LINK)}`
    try {
      location.href = `https://web.whatsapp.com/send?text=${SHARE_TEXT}${encodeURI(
        SHARE_LINK
      )}`;
    } catch (e) {
      addToast({
        title: "Auf deinem Gerät wurde kein Whatsapp gefunden",
        variant: "alert",
      });
    }
  }, [SHARE_LINK, SHARE_TEXT]);

  const shareOnLinkedIn = useCallback(() => {
    try {
      location.href = `https://linkedin.com/sharing/share-offsite/?url=${SHARE_LINK}`;
    } catch (e) {
      addToast({
        title: "Link konnte nicht auf LinkedIn geteilt werden",
        variant: "alert",
      });
    }
  }, [SHARE_LINK, SHARE_TEXT]);

  const shareOnFacebook = useCallback(() => {
    try {
      location.href = `https://www.facebook.com/sharer/sharer.php/?u=${encodeURI(
        SHARE_LINK
      )}`;
    } catch (e) {
      addToast({
        title: "Link konnte nicht auf Facebook geteilt werden",
        variant: "alert",
      });
    }
  }, [SHARE_LINK]);

  const shareOnMailClient = useCallback(() => {
    location.href = `mailto:?body=${SHARE_TEXT}${encodeURI(
      SHARE_LINK
    )}&subject=${SHARE_SUBJECT}`;
  }, [SHARE_LINK, SHARE_TEXT, SHARE_SUBJECT]);

  const saveToClipboard = () => {
    navigator.clipboard
      .writeText(SHARE_LINK)
      .then(() => {
        setCopyText(true);
        setTimeout(() => {
          setCopyText(false);
        }, 2000);
      })
      .catch((err) => {
        addToast({
          title: "Link konnte nicht in Zwischenablage kopiert werden",
          variant: "alert",
        });
      });
  };

  return (
    <Dialog
      open={openPopup}
      onOpenChange={onClose}
      position="center"
      width="narrow"
      className="flex"
    >
      <Slot name="content">
        <div className="p-7 w-full">
          <h2 className="text-center py-6">{popupHeadline}</h2>
          <p className="text-center py-6">
            Jetzt Ihre favorisierten Fahrzeuge mit anderen teilen.
          </p>
          <div className="flex flex-col justify-center items-center py-6">
            <div className="w-full flex items-center justify-center gap-8">
              {whatsapp && (
                <div
                  className="flex flex-col justify-center items-center cursor-pointer"
                  onClick={() =>
                    handleShareClick("whatsapp", "Meine Merkliste")
                  }
                >
                  <Icon type="whatsappCD" className="h-12 w-12" />
                  <small>WhatsApp</small>
                </div>
              )}
              {mail && (
                <div
                  className="flex flex-col justify-center items-center cursor-pointer"
                  onClick={() => handleShareClick("mail", "Meine Merkliste")}
                >
                  <Icon type="mailCD" className="h-12 w-12" />
                  <small>Mail</small>
                </div>
              )}
              {linkedin && (
                <div
                  className="flex flex-col justify-center items-center cursor-pointer"
                  onClick={() =>
                    handleShareClick("linkedin", "Meine Merkliste")
                  }
                >
                  <Icon type="linkedInCD" className="h-12 w-12" />
                  <small>LinkedIn</small>
                </div>
              )}
              {facebook && (
                <div
                  className="flex flex-col justify-center items-center cursor-pointer"
                  onClick={() =>
                    handleShareClick("facebook", "Meine Merkliste")
                  }
                >
                  <Icon type="facebookCD" className="h-12 w-12" />
                  <small>Facebook</small>
                </div>
              )}
            </div>
            <div className="w-full flex gap-6 justify-center py-8">
              <input
                className="border-2 border-blue-light w-full px-6"
                type="text"
                readOnly
                value={SHARE_LINK}
              />
              <Button
                onClick={() => handleShareClick("clipboard", "Meine Merkliste")}
                className={`${
                  copyText && "!bg-success   !border-success"
                } min-w-[145px]`}
              >
                {copyText ? (
                  <p className="flex items-center">
                    Kopiert
                    <Icon type="checkmark" className="h-6 w-6 ml-3" />
                  </p>
                ) : (
                  <p className="flex items-center">Kopieren</p>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Slot>
    </Dialog>
  );
};

type ShareState = "pending" | "success" | "error";

interface Props {
  shareData: ShareData;
  openPopup: boolean;
  onClose: () => void;
  onError?: (error?: unknown) => void;
  whatsapp?: boolean;
  mail?: boolean;
  facebook?: boolean;
  linkedin?: boolean;
  popupHeadline?: string;
}

export default NativeSharePopUp;
